import { msalInstance } from '../authConfig';
import { classifyHub } from '../utils/hubLocation';

class AuthenticatedDataverseService {
  constructor() {
    // Use Azure Function proxy if available, otherwise direct Dataverse access
    this.useProxy = !!process.env.REACT_APP_API_URL;
    this.apiUrl = process.env.REACT_APP_API_URL;
    this.baseUrl = process.env.REACT_APP_DATAVERSE_URL;
    this.sessionsEndpoint = process.env.REACT_APP_SESSIONS_ENDPOINT || 'mi_sessions';
  }

  async getAccessToken() {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        throw new Error('No authenticated accounts found');
      }

      const request = {
        scopes: [`${this.baseUrl.replace('/api/data/v9.2', '')}/user_impersonation`],
        account: accounts[0]
      };

      const response = await msalInstance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      throw error;
    }
  }

  async makeDataverseRequest(endpoint, options = {}) {
    try {
      const token = await this.getAccessToken();
      
      // If using proxy, call Azure Function instead of Dataverse directly
      if (this.useProxy) {
        const url = `${this.apiUrl}/dataverse/${endpoint}`;
        console.log('Using Azure Function proxy:', url);
        
        const response = await fetch(url, {
          ...options,
          headers: {
            'Authorization': `Bearer ${token}`, // User token for authorization
            'Content-Type': 'application/json',
            ...options.headers
          }
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        return { success: true, data: data.value || data, nextLink: data['@odata.nextLink'] || null };
      }
      
      // Direct Dataverse access (original code)
      const defaultHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'OData-MaxVersion': '4.0',
        'OData-Version': '4.0',
        'Prefer': 'odata.include-annotations="*"'
      };

      const requestOptions = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers
        }
      };

      const response = await fetch(`${this.baseUrl}/${endpoint}`, requestOptions);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return { success: true, data: data.value || data, nextLink: data['@odata.nextLink'] || null };
    } catch (error) {
      console.error('Dataverse request failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Fetch all pages of a Dataverse query, following @odata.nextLink
  async fetchAllPages(endpoint, { failOnPageError = false } = {}) {
    let allData = [];
    let currentEndpoint = endpoint;
    let page = 1;

    while (currentEndpoint) {
      console.log(`Fetching page ${page}...`);
      const result = await this.makeDataverseRequest(currentEndpoint);

      if (!result.success) {
        console.error(`Page ${page} failed:`, result.error);
        if (failOnPageError) {
          return { success: false, error: `Page ${page} failed: ${result.error}`, data: allData, totalPages: page - 1 };
        }
        break;
      }

      allData = allData.concat(result.data);
      console.log(`Page ${page}: got ${result.data.length} records (total: ${allData.length})`);

      if (result.nextLink) {
        // nextLink is a full URL; extract just the endpoint portion
        const baseUrl = this.useProxy ? `${this.apiUrl}/dataverse/` : `${this.baseUrl}/`;
        currentEndpoint = result.nextLink.startsWith('http') 
          ? result.nextLink.replace(baseUrl, '').replace(this.baseUrl + '/', '')
          : result.nextLink;
        page++;
      } else {
        currentEndpoint = null;
      }
    }

    return { success: true, data: allData, totalPages: page };
  }

  // Correct field mapping based on your specifications
  getKnownFieldMapping() {
    return {
      // Confirmed field names
      name: 'mi_sessionname',
      startDate: 'mi_startdate',
      endDate: 'mi_enddate',
      location: '_mi_location_value', 
      seatsTaken: 'mi_seatstaken',
      maxCapacity: 'mi_maxcapacity',
      department: 'cr38c_department',
      sessionStatus: 'cr38c_coursecancelled',
      classroom: 'mi_classroom',
      notes: 'cr38c_instructornotes',
      bundledSession: 'mi_bundledsession',
      resources: 'cr38c_resource',
      stateCode: 'statecode',
      statusCode: 'statuscode',
      courseStatus: 'cr38c_coursestatus',
      cancellationReason: 'cr38c_cancellationreason',
      
      // Likely field names
      id: 'mi_sessionid',
      description: 'mi_description',
      maxAttendees: 'mi_maxattendees',
      instructor: 'mi_instructor',
      campus: 'mi_billingresponsibility',
      billingResponsibility: 'mi_billingresponsibility',
      
      // Alternative field names to try
      alternativeDescriptions: [
        'mi_summary', 'mi_details', 'mi_notes'
      ],
      alternativeLocations: [
        'mi_venue', 'mi_room', 'mi_place'
      ],
      alternativeInstructors: [
        'mi_presenter', 'mi_facilitator', 'mi_teacher', 'mi_trainer'
      ]
    };
  }

  async discoverTableSchema() {
    try {
      console.log('Discovering table schema for:', this.sessionsEndpoint);
      
      // Get a few records to see all available fields
      const endpoint = `${this.sessionsEndpoint}?$top=3`;
      const result = await this.makeDataverseRequest(endpoint);
      
      if (result.success && result.data.length > 0) {
        const sampleRecord = result.data[0];
        const allFields = Object.keys(sampleRecord);
        console.log('All available fields:', allFields);
        
        // Use known mapping and discover alternatives
        const knownMapping = this.getKnownFieldMapping();
        const finalMapping = this.buildFieldMapping(allFields, knownMapping);
        
        console.log('Final field mapping:', finalMapping);
        
        return { 
          success: true, 
          data: result.data,
          fieldMap: finalMapping,
          allFields: allFields
        };
      } else {
        return { success: false, error: 'No records found in table' };
      }
    } catch (error) {
      console.error('Error discovering schema:', error);
      return { success: false, error: error.message };
    }
  }

  buildFieldMapping(availableFields, knownMapping) {
    const mapping = {};
    
    // Use the confirmed field names first
    mapping.name = this.findField(availableFields, [knownMapping.name]);
    mapping.startDate = this.findField(availableFields, [knownMapping.startDate]);
    mapping.endDate = this.findField(availableFields, [knownMapping.endDate]);
    
    mapping.id = this.findField(availableFields, [
      knownMapping.id,
      'mi_sessionid',
      'sessionid',
      availableFields.find(f => f.includes('id'))
    ]);
    
    mapping.description = this.findField(availableFields, [
      knownMapping.description,
      ...knownMapping.alternativeDescriptions
    ]);
    
    mapping.location = this.findField(availableFields, [
      knownMapping.location,
      ...knownMapping.alternativeLocations
    ]);
    
    mapping.maxAttendees = this.findField(availableFields, [
      knownMapping.maxAttendees,
      'mi_capacity',
      'mi_size'
    ]);
    
    mapping.instructor = this.findField(availableFields, [
      knownMapping.instructor,
      ...knownMapping.alternativeInstructors
    ]);
    
    mapping.seatsTaken = this.findField(availableFields, [
      knownMapping.seatsTaken
    ]);
    
    mapping.maxCapacity = this.findField(availableFields, [
      knownMapping.maxCapacity
    ]);
    
    mapping.department = this.findField(availableFields, [
      knownMapping.department
    ]);
    
    mapping.sessionStatus = this.findField(availableFields, [
      knownMapping.sessionStatus
    ]);
    
    mapping.classroom = this.findField(availableFields, [
      knownMapping.classroom
    ]);
    
    mapping.notes = this.findField(availableFields, [
      knownMapping.notes
    ]);
    
    mapping.department = this.findField(availableFields, [
      knownMapping.department
    ]);
    
    mapping.sessionStatus = this.findField(availableFields, [
      knownMapping.sessionStatus
    ]);
    
    mapping.classroom = this.findField(availableFields, [
      knownMapping.classroom
    ]);
    
    mapping.billingResponsibility = this.findField(availableFields, [
      knownMapping.billingResponsibility,
      knownMapping.campus
    ]);
    
    mapping.bundledSession = this.findField(availableFields, [
      knownMapping.bundledSession
    ]);
    
    mapping.resources = this.findField(availableFields, [
      knownMapping.resources
    ]);
    
    mapping.stateCode = this.findField(availableFields, [
      knownMapping.stateCode
    ]);
    
    mapping.statusCode = this.findField(availableFields, [
      knownMapping.statusCode
    ]);
    
    mapping.courseStatus = this.findField(availableFields, [
      knownMapping.courseStatus
    ]);
    
    mapping.cancellationReason = this.findField(availableFields, [
      knownMapping.cancellationReason
    ]);
    
    return mapping;
  }

  findField(availableFields, candidateFields) {
    for (const candidate of candidateFields) {
      if (candidate && availableFields.includes(candidate)) {
        return candidate;
      }
    }
    return null;
  }

  // Helper function to normalize session data without breaking up multi-day sessions
  createSessionInstances(session, fieldMap) {
    const startDate = new Date(session[fieldMap.startDate]);
    const endDate = new Date(session[fieldMap.endDate]);
    
    // Check if session spans multiple days
    const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const daysDifference = Math.ceil((endDateOnly - startDateOnly) / (1000 * 60 * 60 * 24));
    
    // Always return a single session instance, whether single or multi-day
    const instance = {
      // Copy all original session data first
      ...session,
      
      // Override with standardized field names
      mi_sessionid: session[fieldMap.id] || `session-${Date.now()}`,
      mi_sessionname: session[fieldMap.name] || 'Untitled Session',
      mi_startdate: session[fieldMap.startDate],
      mi_enddate: session[fieldMap.endDate],
      mi_description: session[fieldMap.description] || '',
      mi_location: session[fieldMap.location] || 'TBD',
      mi_maxattendees: session[fieldMap.maxAttendees] || 0,
      mi_instructor: session[fieldMap.instructor] || 'TBD',
      mi_seatstaken: session[fieldMap.seatsTaken] || 0,
      mi_maxcapacity: session[fieldMap.maxCapacity] || 0,
      cr38c_department: session[fieldMap.department] || '',
      cr38c_coursecancelled: session[fieldMap.sessionStatus] || false,
      mi_classroom: session[fieldMap.classroom] || '',
      mi_notes: session[fieldMap.notes] || '',
      mi_billingresponsibility: session[fieldMap.billingResponsibility] || '',
      mi_bundledsession: session[fieldMap.bundledSession] || false,
      cr38c_resource: session[fieldMap.resources] || '',
      statecode: session[fieldMap.stateCode],
      statuscode: session[fieldMap.statusCode],
      cr38c_coursestatus: session[fieldMap.courseStatus] || '',
      
      // Preserve location GUID
      _mi_location_value: session._mi_location_value,
      
      // Instance metadata
      isMultiDay: daysDifference > 0,
      totalDays: daysDifference + 1
    };
    
    return [instance];
  }

  async getLocationNames(locationGuids) {
    try {
      if (!locationGuids || locationGuids.length === 0) return {};
      
      // Remove duplicates
      const uniqueGuids = [...new Set(locationGuids)];
      
      // Build filter for multiple GUIDs
      const guidFilters = uniqueGuids.map(guid => `mi_locationid eq ${guid}`).join(' or ');
      
      // Updated to use mi_locationname instead of mi_locationname
      const endpoint = `mi_locations?$select=mi_locationid,mi_locationname&$filter=${guidFilters}`;
      console.log('Fetching location names with endpoint:', endpoint);
      
      const result = await this.makeDataverseRequest(endpoint);
      
      if (result.success) {
        console.log('Raw location data:', result.data);
        
        // Create a lookup map: GUID -> Name (using mi_locationname)
        const locationMap = {};
        result.data.forEach(location => {
          locationMap[location.mi_locationid] = location.mi_locationname;
        });
        
        console.log('Location name map:', locationMap);
        return locationMap;
      }
      
      return {};
    } catch (error) {
      console.error('Error fetching location names:', error);
      return {};
    }
  }

  async getDepartmentNames(departmentGuids) {
    // The cr38c_departments table does not exist in this Dataverse environment.
    // Department display names are already resolved via the @Microsoft.Dynamics.CRM.FormattedValue
    // annotation on the cr38c_department lookup, so a separate name lookup is unnecessary
    // and was producing noisy 404s on every page load. Short-circuit to an empty map.
    return {};
  }

  async getEventInstructors(eventGuids) {
    try {
      if (!eventGuids || eventGuids.length === 0) return {};
      
      // Remove duplicates
      const uniqueGuids = [...new Set(eventGuids)];
      
      // Build filter for multiple event GUIDs
      const guidFilters = uniqueGuids.map(guid => `_cr38c_event_value eq ${guid}`).join(' or ');
      
      const endpoint = `cr38c_eventinstructorses?$select=cr38c_eventinstructorsid,_cr38c_event_value,_cr38c_instructor_value&$filter=${guidFilters}`;
      console.log('Fetching event instructors with endpoint:', endpoint);
      
      const result = await this.makeDataverseRequest(endpoint);
      
      if (result.success) {
        console.log('Raw event instructor data:', result.data);
        
        // Group instructors by event GUID
        const eventInstructorMap = {};
        result.data.forEach(instructor => {
          const eventGuid = instructor._cr38c_event_value;
          if (!eventInstructorMap[eventGuid]) {
            eventInstructorMap[eventGuid] = [];
          }
          eventInstructorMap[eventGuid].push({
            contactGuid: instructor._cr38c_instructor_value,
            isPrimary: false // Events don't have primary designation
          });
        });
        
        console.log('Event instructor map:', eventInstructorMap);
        return eventInstructorMap;
      }
      
      return {};
    } catch (error) {
      console.error('Error fetching event instructors:', error);
      return {};
    }
  }

  async getSessionInstructors(sessionGuids) {
    try {
      if (!sessionGuids || sessionGuids.length === 0) return {};
      
      // Remove duplicates
      const uniqueGuids = [...new Set(sessionGuids)];
      
      // Batch into chunks to avoid URL length limits
      const batchSize = 25;
      const sessionInstructorMap = {};
      
      for (let i = 0; i < uniqueGuids.length; i += batchSize) {
        const batch = uniqueGuids.slice(i, i + batchSize);
        const guidFilters = batch.map(guid => `_mi_session_value eq ${guid}`).join(' or ');
        
        const endpoint = `mi_instructors?$select=_mi_session_value,_mi_instructor_value,mi_primary,cr38c_role,cr38c_startdate,cr38c_enddate&$filter=${guidFilters}`;
        console.log(`Fetching instructors batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueGuids.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          // Group instructors by session GUID
          result.data.forEach(instructor => {
            const sessionGuid = instructor._mi_session_value;
            const roleFormatted = instructor['cr38c_role@Microsoft.Dynamics.CRM.FormattedValue'] || 
                                  instructor['cr38c_role@OData.Community.Display.V1.FormattedValue'];
            if (!sessionInstructorMap[sessionGuid]) {
              sessionInstructorMap[sessionGuid] = [];
            }
            sessionInstructorMap[sessionGuid].push({
              contactGuid: instructor._mi_instructor_value,
              isPrimary: instructor.mi_primary,
              role: roleFormatted || instructor.cr38c_role,
              startDate: instructor.cr38c_startdate,
              endDate: instructor.cr38c_enddate
            });
          });
        }
      }
      
      console.log(`Fetched instructors for ${Object.keys(sessionInstructorMap).length} sessions`);
      return sessionInstructorMap;
    } catch (error) {
      console.error('Error fetching session instructors:', error);
      return {};
    }
  }

  async getContactNames(contactGuids) {
    try {
      if (!contactGuids || contactGuids.length === 0) return {};
      
      // Remove duplicates
      const uniqueGuids = [...new Set(contactGuids)];
      
      // Batch into chunks to avoid URL length limits
      const batchSize = 50;
      const contactMap = {};
      
      for (let i = 0; i < uniqueGuids.length; i += batchSize) {
        const batch = uniqueGuids.slice(i, i + batchSize);
        const guidFilters = batch.map(guid => `contactid eq ${guid}`).join(' or ');
        
        const endpoint = `contacts?$select=contactid,fullname&$filter=${guidFilters}`;
        console.log(`Fetching contacts batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueGuids.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          result.data.forEach(contact => {
            contactMap[contact.contactid] = contact.fullname;
          });
        }
      }
      
      console.log(`Fetched ${Object.keys(contactMap).length} contact names`);
      return contactMap;
    } catch (error) {
      console.error('Error fetching contact names:', error);
      return {};
    }
  }

  async getSessionResources(sessionIds) {
    try {
      if (!sessionIds || sessionIds.length === 0) return {};
      
      // Remove duplicates
      const uniqueIds = [...new Set(sessionIds)];
      
      // Batch into chunks to avoid URL length limits
      const batchSize = 25;
      const sessionResourceMap = {};
      
      for (let i = 0; i < uniqueIds.length; i += batchSize) {
        const batch = uniqueIds.slice(i, i + batchSize);
        const idFilters = batch.map(id => `_cr38c_mi_session_value eq ${id}`).join(' or ');
        
        const endpoint = `cr38c_sessionresources?$select=_cr38c_mi_session_value,_cr38c_assignedresource_value&$filter=${idFilters}`;
        console.log(`Fetching resources batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueIds.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          // Group resource IDs by session ID
          result.data.forEach(resource => {
            const sessionId = resource._cr38c_mi_session_value;
            const resourceId = resource._cr38c_assignedresource_value;
            if (!sessionResourceMap[sessionId]) {
              sessionResourceMap[sessionId] = [];
            }
            sessionResourceMap[sessionId].push(resourceId);
          });
        }
      }
      
      console.log(`Fetched resources for ${Object.keys(sessionResourceMap).length} sessions`);
      return sessionResourceMap;
    } catch (error) {
      console.error('Error fetching session resources:', error);
      return {};
    }
  }

  async getResourceNames(resourceIds) {
    try {
      if (!resourceIds || resourceIds.length === 0) return {};
      
      // Remove duplicates
      const uniqueIds = [...new Set(resourceIds)];
      
      // Batch into chunks to avoid URL length limits
      const batchSize = 50;
      const resourceMap = {};
      
      for (let i = 0; i < uniqueIds.length; i += batchSize) {
        const batch = uniqueIds.slice(i, i + batchSize);
        const idFilters = batch.map(id => `cr38c_resourceid eq ${id}`).join(' or ');
        
        const endpoint = `cr38c_resources?$select=cr38c_resourceid,cr38c_recourse_id,statecode&$filter=(${idFilters}) and statecode eq 0`;
        console.log(`Fetching resource names batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueIds.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          result.data.forEach(resource => {
            resourceMap[resource.cr38c_resourceid] = resource.cr38c_recourse_id;
          });
        }
      }
      
      console.log(`Fetched ${Object.keys(resourceMap).length} resource names`);
      return resourceMap;
    } catch (error) {
      console.error('Error fetching resource names:', error);
      return {};
    }
  }

  // Get count of account-only registrations (account set but no student) for sessions
  // Returns { sessionId: { placeholders: X, waitlist: Y, total: Z } }
  // Placeholders = account-only registrations (excluding Waitlist account)
  // Waitlist = registrations with account named "Waitlist" and no student
  async getAccountOnlyRegistrationCounts(sessionIds) {
    try {
      if (!sessionIds || sessionIds.length === 0) return {};
      
      // Remove duplicates
      const uniqueIds = [...new Set(sessionIds)];
      
      // Batch into chunks to avoid URL length limits
      const batchSize = 25;
      const registrationsBySession = {};
      const accountGuids = new Set();
      
      for (let i = 0; i < uniqueIds.length; i += batchSize) {
        const batch = uniqueIds.slice(i, i + batchSize);
        const sessionFilters = batch.map(id => `_mi_session_value eq ${id}`).join(' or ');
        
        // Query registrations: account exists AND student is null AND not dropped
        const endpoint = `mi_registrations?$select=mi_registrationid,_mi_session_value,_mi_account_value,_mi_student_value,statuscode&$filter=(${sessionFilters}) and _mi_account_value ne null and _mi_student_value eq null and statuscode ne 2`;
        console.log(`Fetching account-only registrations batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueIds.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          result.data.forEach(reg => {
            const sessionId = reg._mi_session_value;
            const accountGuid = reg._mi_account_value;
            
            if (!registrationsBySession[sessionId]) {
              registrationsBySession[sessionId] = [];
            }
            registrationsBySession[sessionId].push(accountGuid);
            
            if (accountGuid) {
              accountGuids.add(accountGuid);
            }
          });
        }
      }
      
      // Fetch account names to identify "Waitlist" accounts
      const accountNameMap = {};
      if (accountGuids.size > 0) {
        const accountGuidArray = [...accountGuids];
        const accountBatchSize = 50;
        
        for (let i = 0; i < accountGuidArray.length; i += accountBatchSize) {
          const batch = accountGuidArray.slice(i, i + accountBatchSize);
          const guidFilters = batch.map(guid => `accountid eq ${guid}`).join(' or ');
          
          const endpoint = `accounts?$select=accountid,name&$filter=${guidFilters}`;
          console.log(`Fetching account names batch ${Math.floor(i/accountBatchSize) + 1}/${Math.ceil(accountGuidArray.length/accountBatchSize)}`);
          
          const result = await this.makeDataverseRequest(endpoint);
          
          if (result.success) {
            result.data.forEach(account => {
              accountNameMap[account.accountid] = account.name;
            });
          }
        }
      }
      
      // Build the final count map with placeholders vs waitlist
      const accountOnlyCountMap = {};
      
      for (const [sessionId, accountGuidList] of Object.entries(registrationsBySession)) {
        let placeholders = 0;
        let waitlist = 0;
        
        accountGuidList.forEach(accountGuid => {
          const accountName = accountNameMap[accountGuid] || '';
          if (accountName.toLowerCase() === 'waitlist') {
            waitlist++;
          } else {
            placeholders++;
          }
        });
        
        accountOnlyCountMap[sessionId] = {
          placeholders,
          waitlist,
          total: placeholders + waitlist
        };
      }
      
      console.log('=== ACCOUNT-ONLY REGISTRATION COUNTS ===');
      console.log(`Sessions with account-only registrations: ${Object.keys(accountOnlyCountMap).length}`);
      console.log('Sample counts:', Object.entries(accountOnlyCountMap).slice(0, 5));
      console.log('=== END ACCOUNT-ONLY COUNTS ===');
      return accountOnlyCountMap;
    } catch (error) {
      console.error('Error fetching account-only registration counts:', error);
      return {};
    }
  }

  async getBundledSessions(parentSessionIds) {
    try {
      if (!parentSessionIds || parentSessionIds.length === 0) return {};
      
      // Remove duplicates
      const uniqueIds = [...new Set(parentSessionIds)];
      
      // Batch into chunks of 25 for faster parallel processing
      const batchSize = 25;
      const bundleMap = {};
      
      for (let i = 0; i < uniqueIds.length; i += batchSize) {
        const batch = uniqueIds.slice(i, i + batchSize);
        const idFilters = batch.map(id => `_mi_parentsession_value eq ${id}`).join(' or ');
        
        const endpoint = `mi_bundledsessions?$select=mi_bundledsessionid,_mi_parentsession_value,_mi_includedsession_value&$filter=${idFilters}`;
        console.log(`Fetching bundled sessions batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(uniqueIds.length/batchSize)}`);
        
        const result = await this.makeDataverseRequest(endpoint);
        
        if (result.success) {
          // Group child session IDs by parent session ID
          result.data.forEach(bundle => {
            const parentId = bundle._mi_parentsession_value;
            const childId = bundle._mi_includedsession_value;
            if (!bundleMap[parentId]) {
              bundleMap[parentId] = [];
            }
            bundleMap[parentId].push(childId);
          });
        }
      }
      
      console.log('=== BUNDLE MAP SUMMARY ===');
      console.log(`Total parent sessions: ${Object.keys(bundleMap).length}`);
      console.log(`Total child sessions: ${Object.values(bundleMap).flat().length}`);
      console.log('Sample bundle mapping:', Object.entries(bundleMap).slice(0, 3));
      console.log('=== END BUNDLE MAP SUMMARY ===');
      return bundleMap;
    } catch (error) {
      console.error('Error fetching bundled sessions:', error);
      return {};
    }
  }


  getCalendarEndDate() {
    const twelveMonthsFromNow = new Date();
    twelveMonthsFromNow.setMonth(twelveMonthsFromNow.getMonth() + 12);
    // Include all of 2027 in local calendar time without shortening the rolling horizon.
    return new Date(Math.max(twelveMonthsFromNow.getTime(), new Date(2028, 0, 1).getTime() - 1));
  }

  async getEvents() {
    try {
      console.log('Starting getEvents...');
      
      // First, try to get a few events to check table structure
      const testEndpoint = `cr38c_events?$top=3`;
      console.log('Testing events table with endpoint:', testEndpoint);
      
      const testResult = await this.makeDataverseRequest(testEndpoint);
      
      if (!testResult.success) {
        console.error('Events table test failed:', testResult.error);
        return { success: false, error: `Events table access failed: ${testResult.error}` };
      }
      
      console.log('Events table accessible. Sample data:', testResult.data);
      
      if (testResult.data.length > 0) {
        const sampleEvent = testResult.data[0];
        console.log('Available event fields:', Object.keys(sampleEvent));
      }
      
      // Build query for events with confirmed field names
      const selectFields = [
        'cr38c_eventid',
        'cr38c_eventname', // Title
        'cr38c_eventstartdate',
        'cr38c_eventenddate', 
        'cr38c_eventnotes',
        'cr38c_assignedcampus',
        'cr38c_department',
        'cr38c_classroom',
        '_cr38c_assignedlocation_value', // Location lookup to mi_locations table
        // '_cr38c_instructor_value', // Removed - field doesn't exist in maritimeinstitute environment
        'statecode',
        'statuscode'
      ];
      
      let query = `$select=${selectFields.join(',')}`;
      
      // Past 30 days through at least the end of 2027.
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const calendarEndDate = this.getCalendarEndDate();
      
      query += `&$filter=cr38c_eventstartdate ge ${thirtyDaysAgo.toISOString()} and cr38c_eventstartdate le ${calendarEndDate.toISOString()}`;
      query += `&$orderby=cr38c_eventstartdate asc`;

      const endpoint = `cr38c_events?${query}`;
      console.log('Fetching events with endpoint:', endpoint);
      
      const result = await this.fetchAllPages(endpoint, { failOnPageError: true });
      
      if (!result.success) {
        console.error('Events query failed with error:', result.error);
        console.error('Failed endpoint was:', endpoint);
        
        // Try simpler query without filters
        console.log('Trying simpler events query...');
        const simpleEndpoint = `cr38c_events?$top=10`;
        const simpleResult = await this.makeDataverseRequest(simpleEndpoint);
        
        if (simpleResult.success) {
          console.log('Simple events query worked. Available fields:', Object.keys(simpleResult.data[0] || {}));
          return { success: false, error: 'Complex query failed but table is accessible' };
        } else {
          console.error('Even simple events query failed:', simpleResult.error);
          return simpleResult;
        }
      }
      
      if (result.success) {
        console.log(`Raw event data (${result.data.length} records):`, result.data.slice(0, 3));
        
        // Debug: Check classroom data in events
        if (result.data.length > 0) {
          console.log('Sample event classroom data:', {
            raw: result.data[0].cr38c_classroom,
            formatted: result.data[0]['cr38c_classroom@Microsoft.Dynamics.CRM.FormattedValue']
          });
        }
        
        // Transform events to match session structure for calendar compatibility
        const transformedEvents = result.data.map(event => {
          // Get formatted values for choice fields
          const campusFormatted = event[`cr38c_assignedcampus@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                  event[`cr38c_assignedcampus@OData.Community.Display.V1.FormattedValue`];
          const departmentFormatted = event[`cr38c_department@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                      event[`cr38c_department@OData.Community.Display.V1.FormattedValue`];
          const classroomFormatted = event[`cr38c_classroom@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                     event[`cr38c_classroom@OData.Community.Display.V1.FormattedValue`];
          const locationFormatted = event[`_cr38c_assignedlocation_value@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                    event[`_cr38c_assignedlocation_value@OData.Community.Display.V1.FormattedValue`];
          
          return {
            // Event-specific fields
            cr38c_eventid: event.cr38c_eventid,
            mi_sessionname: event.cr38c_eventname || 'Untitled Event',
            mi_startdate: event.cr38c_eventstartdate,
            mi_enddate: event.cr38c_eventenddate,
            mi_notes: event.cr38c_eventnotes || '',
            mi_billingresponsibility: event.cr38c_assignedcampus || '',
            campusName: campusFormatted, // Add formatted campus name
            cr38c_department: event.cr38c_department || '',
            departmentName: departmentFormatted, // Add formatted department name
            mi_classroom: event.cr38c_classroom || '',
            classroomName: classroomFormatted, // Add formatted classroom name
            _mi_location_value: event._cr38c_assignedlocation_value || '',
            locationName: locationFormatted || '', // Location name from mi_locations lookup
            mi_instructor: event._cr38c_instructor_value || '',
            _cr38c_instructor_value: event._cr38c_instructor_value, // Store GUID for lookup
            statecode: event.statecode,
            statuscode: event.statuscode,
            
            // Event metadata
            isEvent: true,
            isMultiDay: false, // Calculate if needed
            
            // Default values for session fields
            mi_seatstaken: null,
            mi_maxcapacity: null,
            cr38c_coursecancelled: false,
            instructors: []
          };
        });
        
        console.log(`Transformed ${transformedEvents.length} events`);
        console.log('Sample transformed event:', transformedEvents[0]);
        
        return { 
          success: true, 
          data: transformedEvents,
          originalCount: result.data.length
        };
      } else {
        return result;
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      return { success: false, error: error.message };
    }
  }

  async getSessionsBatch(startDays, endDays) {
    // Load sessions for a specific date range (days from now)
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + startDays);
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + endDays);
    
    console.log(`Fetching batch: ${startDays} to ${endDays} days from now`);
    
    const schemaResult = await this.discoverTableSchema();
    if (!schemaResult.success) return schemaResult;
    
    const { fieldMap } = schemaResult;
    const selectFields = [...new Set(Object.values(fieldMap).filter(Boolean))];
    
    let query = `$top=500&$orderby=${fieldMap.startDate} asc`;
    if (selectFields.length > 0) {
      query += `&$select=${selectFields.join(',')}`;
    }
    query += `&$filter=${fieldMap.startDate} ge ${startDate.toISOString()} and ${fieldMap.startDate} le ${endDate.toISOString()}`;
    
    const endpoint = `${this.sessionsEndpoint}?${query}`;
    const result = await this.makeDataverseRequest(endpoint);
    
    if (result.success) {
      // Simplified processing - skip child sessions, instructors, resources for batches
      const sessions = result.data.map(session => {
        const instances = this.createSessionInstances(session, fieldMap);
        return instances.map(instance => ({
          ...instance,
          mi_notes: session[fieldMap.notes] || '',
          locationName: session[`${fieldMap.location}@Microsoft.Dynamics.CRM.FormattedValue`] || '',
          classroomName: session[`${fieldMap.classroom}@Microsoft.Dynamics.CRM.FormattedValue`] || '',
          instructors: [] // Defer instructor loading
        }));
      }).flat();
      
      return { success: true, data: sessions };
    }
    
    return result;
  }

  async getTrainingSessions(priorityMode = false) {
  try {
    // Discover schema first
    const schemaResult = await this.discoverTableSchema();
    
    if (!schemaResult.success) {
      return schemaResult;
    }

    const { fieldMap } = schemaResult;
    console.log('Using field mapping:', fieldMap);

    // Build select clause with discovered fields and remove duplicates
    const selectFields = [...new Set(Object.values(fieldMap).filter(Boolean))];
    
    // Keep the fast priority cap; the full load must retrieve every matching page.
    let query = priorityMode ? `$top=1000` : '';
    
    if (selectFields.length > 0) {
      query += `${query ? '&' : ''}$select=${selectFields.join(',')}`;
    }
    
    // Add ordering if we have a date field
    if (fieldMap.startDate) {
      query += `${query ? '&' : ''}$orderby=${fieldMap.startDate} asc`;
      
      if (priorityMode) {
        // Priority mode: 30 days ago to +60 days
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const sixtyDaysFromNow = new Date();
        sixtyDaysFromNow.setDate(sixtyDaysFromNow.getDate() + 60);
        
        query += `&$filter=(${fieldMap.startDate} ge ${thirtyDaysAgo.toISOString()} or ${fieldMap.endDate} ge ${thirtyDaysAgo.toISOString()}) and ${fieldMap.startDate} le ${sixtyDaysFromNow.toISOString()}`;
        console.log('Priority mode: loading -30 days to +60 days');
      } else {
        // Full mode: past 1 month through at least the end of 2027 (background load).
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
        const calendarEndDate = this.getCalendarEndDate();
        
        query += `&$filter=(${fieldMap.startDate} ge ${oneMonthAgo.toISOString()} or ${fieldMap.endDate} ge ${oneMonthAgo.toISOString()}) and ${fieldMap.startDate} le ${calendarEndDate.toISOString()}`;
        console.log('Full mode: loading -1 month through', calendarEndDate.toISOString());
      }
    }

    const endpoint = `${this.sessionsEndpoint}?${query}`;
    console.log('Fetching with endpoint:', endpoint);
    
    const result = priorityMode
      ? await this.makeDataverseRequest(endpoint)
      : await this.fetchAllPages(endpoint, { failOnPageError: true });
    
    if (result.success) {
      // Backend filter: exclude any session whose cr38c_cancellationreason contains 'Rescheduled'.
      // Checks raw value plus OData formatted-value annotations to support both text and choice fields.
      const beforeCount = result.data.length;
      result.data = result.data.filter(session => {
        const raw = session.cr38c_cancellationreason;
        const formatted = session['cr38c_cancellationreason@OData.Community.Display.V1.FormattedValue']
          || session['cr38c_cancellationreason@Microsoft.Dynamics.CRM.FormattedValue'];
        const candidate = (formatted != null ? String(formatted) : (raw != null ? String(raw) : ''));
        return !candidate.toLowerCase().includes('rescheduled');
      });
      const removed = beforeCount - result.data.length;
      if (removed > 0) {
        console.log(`Backend filter removed ${removed} session(s) with cancellation reason containing 'Rescheduled'`);
      }
      
      console.log(`Raw session data (${result.data.length} records):`, result.data.slice(0, 3));
      
      // Debug: Check what formatted value keys are available
      if (result.data.length > 0) {
        const sampleSession = result.data[0];
        const formattedKeys = Object.keys(sampleSession).filter(key => key.includes('FormattedValue'));
        console.log('Available formatted value keys:', formattedKeys);
      }
      
      // Extract unique location GUIDs for lookup
      const locationGuids = result.data
        .map(session => session._mi_location_value)
        .filter(Boolean);
      
      // Extract unique department GUIDs for lookup (if department is a lookup field)
      const departmentGuids = result.data
        .map(session => session[fieldMap.department])
        .filter(Boolean);
      
      // Extract session GUIDs for instructor lookup
      const sessionIds = result.data
        .map(session => session[fieldMap.id])
        .filter(Boolean);
      
      console.log('Found location GUIDs:', locationGuids);
      console.log('Found department GUIDs:', departmentGuids);
      console.log('Found session GUIDs for instructor lookup:', sessionIds);
      
      // Fetch location, department, instructor, and resource data
      let locationMap = {};
      let departmentMap = {};
      let sessionInstructorMap = {};
      let contactMap = {};
      let sessionResourceMap = {};
      let resourceNameMap = {};
      let bundleMap = {};
      let childSessionsMap = {};
      let accountOnlyCountMap = {};
      
      // Always load instructors (needed for filter), skip other lookups in priority mode
      if (sessionIds.length > 0) {
        sessionInstructorMap = await this.getSessionInstructors(sessionIds);
        
        // Get all contact GUIDs from instructor assignments
        const allContactGuids = Object.values(sessionInstructorMap)
          .flat()
          .map(instructor => instructor.contactGuid)
          .filter(Boolean);
          
        if (allContactGuids.length > 0) {
          contactMap = await this.getContactNames(allContactGuids);
        }
      }
      
      // Always get bundled session relationships to filter children from main view
      const parentSessionIds = result.data
        .filter(session => session[fieldMap.bundledSession])
        .map(session => session[fieldMap.id])
        .filter(Boolean);
      
      if (parentSessionIds.length > 0) {
        bundleMap = await this.getBundledSessions(parentSessionIds);
        console.log('Bundle map result (for filtering):', Object.keys(bundleMap).length, 'parents');
      }
      
      // Skip location/department/resource lookups in priority mode for faster initial load
      if (!priorityMode) {
        if (locationGuids.length > 0) {
          locationMap = await this.getLocationNames(locationGuids);
        }
        
        if (departmentGuids.length > 0) {
          departmentMap = await this.getDepartmentNames(departmentGuids);
        }
        
        // Get account-only registration counts to adjust seats taken
        // These are registrations with an account but no student assigned
        if (sessionIds.length > 0) {
          accountOnlyCountMap = await this.getAccountOnlyRegistrationCounts(sessionIds);
        }
        
        // Get resource assignments for sessions
        if (sessionIds.length > 0) {
        sessionResourceMap = await this.getSessionResources(sessionIds);
        
        // Get all resource IDs from assignments
        const allResourceIds = Object.values(sessionResourceMap)
          .flat()
          .filter(Boolean);
          
        if (allResourceIds.length > 0) {
          resourceNameMap = await this.getResourceNames(allResourceIds);
        }
      }
      
      // Get all child session IDs from bundle map (already fetched above)
      const allChildIds = Object.values(bundleMap).flat().filter(Boolean);
      
      // Fetch child session details for modal display (only in full mode)
      if (allChildIds.length > 0) {
        console.log(`Fetching ${allChildIds.length} child session details...`);
        
        // Batch child session fetches for faster parallel processing
          const batchSize = 25;
          const allChildSessionData = [];
          
          for (let i = 0; i < allChildIds.length; i += batchSize) {
            const batch = allChildIds.slice(i, i + batchSize);
            const childIdFilters = batch.map(id => `mi_sessionid eq ${id}`).join(' or ');
            const childEndpoint = `${this.sessionsEndpoint}?$select=${selectFields.join(',')}&$filter=${childIdFilters}`;
            
            console.log(`Fetching child sessions batch ${Math.floor(i/batchSize) + 1}/${Math.ceil(allChildIds.length/batchSize)}`);
            const childResult = await this.makeDataverseRequest(childEndpoint);
            
            if (childResult.success) {
              // Backend filter: also exclude child sessions whose cr38c_cancellationreason contains 'Rescheduled'
              const filteredChildren = childResult.data.filter(session => {
                const raw = session.cr38c_cancellationreason;
                const formatted = session['cr38c_cancellationreason@OData.Community.Display.V1.FormattedValue']
                  || session['cr38c_cancellationreason@Microsoft.Dynamics.CRM.FormattedValue'];
                const candidate = (formatted != null ? String(formatted) : (raw != null ? String(raw) : ''));
                return !candidate.toLowerCase().includes('rescheduled');
              });
              allChildSessionData.push(...filteredChildren);
            }
          }
          
          if (allChildSessionData.length > 0) {
            // Fetch account-only registration counts for child sessions
            const childSessionIds = allChildSessionData.map(s => s[fieldMap.id]).filter(Boolean);
            let childAccountOnlyCountMap = {};
            if (childSessionIds.length > 0) {
              childAccountOnlyCountMap = await this.getAccountOnlyRegistrationCounts(childSessionIds);
            }
            
            // Process child sessions and group by parent
            allChildSessionData.forEach(childSession => {
              const childInstances = this.createSessionInstances(childSession, fieldMap);
              
              // Add lookup data to child instances
              childInstances.forEach(instance => {
                const locationGuid = instance._mi_location_value;
                const departmentGuid = instance[fieldMap.department];
                const sessionId = instance[fieldMap.id];
                
                // Apply same formatting as parent sessions
                const classroomFormatted = childSession[`${fieldMap.classroom}@Microsoft.Dynamics.CRM.FormattedValue`];
                const departmentFormatted = childSession[`${fieldMap.department}@Microsoft.Dynamics.CRM.FormattedValue`];
                const campusFormatted = childSession[`${fieldMap.billingResponsibility}@Microsoft.Dynamics.CRM.FormattedValue`];
                const locationFormatted = childSession[`${fieldMap.location}@Microsoft.Dynamics.CRM.FormattedValue`];
                
                if (locationFormatted) instance.locationName = locationFormatted;
                else if (locationGuid && locationMap[locationGuid]) instance.locationName = locationMap[locationGuid];
                
                if (departmentFormatted) instance.departmentName = departmentFormatted;
                else if (departmentGuid && departmentMap[departmentGuid]) instance.departmentName = departmentMap[departmentGuid];
                
                if (classroomFormatted) instance.classroomName = classroomFormatted;
                if (campusFormatted) instance.campusName = campusFormatted;
                
                // Add instructor data
                const instructorsForSession = sessionInstructorMap[sessionId] || [];
                const instructorList = instructorsForSession.map(instructor => ({
                  name: contactMap[instructor.contactGuid] || 'Unknown Instructor',
                  isPrimary: instructor.isPrimary,
                  contactGuid: instructor.contactGuid,
                  role: instructor.role,
                  startDate: instructor.startDate,
                  endDate: instructor.endDate
                }));
                instructorList.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
                instance.instructors = instructorList;
                
                if (instructorList.length > 0) {
                  const primaryInstructor = instructorList.find(i => i.isPrimary) || instructorList[0];
                  instance.mi_instructor = primaryInstructor.name;
                }
                
                // Add placeholder and waitlist counts for child sessions
                const childAccountOnlyCounts = childAccountOnlyCountMap[sessionId] || { placeholders: 0, waitlist: 0, total: 0 };
                instance.placeholderCount = childAccountOnlyCounts.placeholders;
                instance.waitlistCount = childAccountOnlyCounts.waitlist;
                
                if (childAccountOnlyCounts.total > 0) {
                  const originalSeatsTaken = instance.mi_seatstaken || 0;
                  instance.mi_seatstaken = Math.max(0, originalSeatsTaken - childAccountOnlyCounts.total);
                  console.log(`📊 Adjusted child session seats for "${instance.mi_sessionname}": ${originalSeatsTaken} - ${childAccountOnlyCounts.total} = ${instance.mi_seatstaken}`);
                }
              });
              
              // Find parent session ID for this child
              for (const [parentId, childIds] of Object.entries(bundleMap)) {
                if (childIds.includes(childSession[fieldMap.id])) {
                  if (!childSessionsMap[parentId]) {
                    childSessionsMap[parentId] = [];
                  }
                  childSessionsMap[parentId].push(...childInstances);
                  break;
                }
              }
            });
          }
        }
      } // End of !priorityMode check
      
      // Debug location mapping
      console.log('=== LOCATION DEBUG ===');
      console.log('Available location GUIDs in map:', Object.keys(locationMap));
      console.log('Session location GUIDs found:', locationGuids);

      // Check for mismatches
      const sessionGuids = result.data.map(s => s._mi_location_value).filter(Boolean);
      const unmappedGuids = sessionGuids.filter(guid => !locationMap[guid]);
      if (unmappedGuids.length > 0) {
        console.log('❌ Unmapped GUIDs found:', unmappedGuids);
      }
      console.log('=== END LOCATION DEBUG ===');

      // Transform and handle multi-day sessions
      const allSessionInstances = [];
      
      result.data.forEach(session => {
        if (session[fieldMap.startDate]) {
          const instances = this.createSessionInstances(session, fieldMap);
          
          // Add location, department, classroom, and instructor data to each instance
         instances.forEach(instance => {
          const locationGuid = instance._mi_location_value;
          const departmentGuid = instance[fieldMap.department];
          const sessionId = instance[fieldMap.id];
          
          // Get formatted values for choice fields (these come automatically with Prefer header)
          const classroomFormatted = session[`${fieldMap.classroom}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                     session[`${fieldMap.classroom}@OData.Community.Display.V1.FormattedValue`];
          const departmentFormatted = session[`${fieldMap.department}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                      session[`${fieldMap.department}@OData.Community.Display.V1.FormattedValue`];
          const campusFormatted = session[`${fieldMap.billingResponsibility}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                  session[`${fieldMap.billingResponsibility}@OData.Community.Display.V1.FormattedValue`];
          const locationFormatted = session[`${fieldMap.location}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                    session[`${fieldMap.location}@OData.Community.Display.V1.FormattedValue`];
          const resourcesFormatted = session[`${fieldMap.resources}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                     session[`${fieldMap.resources}@OData.Community.Display.V1.FormattedValue`];
          const courseStatusFormatted = session[`${fieldMap.courseStatus}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                        session[`${fieldMap.courseStatus}@OData.Community.Display.V1.FormattedValue`];
          
          // Get instructor data for this session
          const instructorsForSession = sessionInstructorMap[sessionId] || [];
          const instructorList = instructorsForSession.map(instructor => ({
            name: contactMap[instructor.contactGuid] || 'Unknown Instructor',
            isPrimary: instructor.isPrimary,
            contactGuid: instructor.contactGuid,
            role: instructor.role,
            startDate: instructor.startDate,
            endDate: instructor.endDate
          }));
          
          // Sort instructors so primary comes first
          instructorList.sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
          
          console.log('Processing instance:', {
            sessionName: instance[fieldMap.name],
            sessionId: sessionId,
            locationGuid: locationGuid,
            departmentGuid: departmentGuid,
            classroomFormatted: classroomFormatted,
            departmentFormatted: departmentFormatted,
            instructorCount: instructorList.length,
            instructors: instructorList,
            availableInLocationMap: locationGuid ? locationMap[locationGuid] : 'no guid',
            availableInDepartmentMap: departmentGuid ? departmentMap[departmentGuid] : 'no guid'
          });
  
          // Use formatted value for location (choice field) or lookup value
          if (locationFormatted) {
            instance.locationName = locationFormatted;
            console.log(`✅ Using formatted location value: "${locationFormatted}"`);
          } else if (locationGuid && locationMap[locationGuid]) {
            instance.locationName = locationMap[locationGuid];
            console.log(`✅ Successfully mapped location ${locationGuid} to "${locationMap[locationGuid]}"`);
          } else if (locationGuid) {
            console.log(`❌ No location mapping found for GUID: ${locationGuid}`);
          }
          
          // Use formatted value for department (choice field) or lookup value
          if (departmentFormatted) {
            instance.departmentName = departmentFormatted;
            console.log(`✅ Using formatted department value: "${departmentFormatted}"`);
          } else if (departmentGuid && departmentMap[departmentGuid]) {
            instance.departmentName = departmentMap[departmentGuid];
            console.log(`✅ Successfully mapped department ${departmentGuid} to "${departmentMap[departmentGuid]}"`);
          } else if (departmentGuid) {
            console.log(`❌ No department mapping found for GUID: ${departmentGuid}`);
          }
          
          // Use formatted value for classroom (choice field)
          if (classroomFormatted) {
            instance.classroomName = classroomFormatted;
            console.log(`✅ Using formatted classroom value: "${classroomFormatted}"`);
          } else {
            console.log(`❌ No formatted classroom value found`);
          }
          
          // Use formatted value for campus (choice field)
          if (campusFormatted) {
            instance.campusName = campusFormatted;
            console.log(`✅ Using formatted campus value: "${campusFormatted}"`);
          } else {
            console.log(`❌ No formatted campus value found`);
          }
          
          // Use formatted value for resources (choice field)
          if (resourcesFormatted) {
            instance.resourcesName = resourcesFormatted;
            console.log(`✅ Using formatted resources value: "${resourcesFormatted}"`);
          } else {
            console.log(`❌ No formatted resources value found`);
          }
          
          // Use formatted value for cr38c_coursestatus (choice field)
          if (courseStatusFormatted) {
            instance.courseStatusName = courseStatusFormatted;
            console.log(`✅ Using formatted cr38c_coursestatus value: "${courseStatusFormatted}"`);
          } else {
            console.log(`❌ No formatted cr38c_coursestatus value found`);
          }
          
          // Add instructor list to instance
          instance.instructors = instructorList;
          
          // Keep backward compatibility with single instructor field
          if (instructorList.length > 0) {
            const primaryInstructor = instructorList.find(i => i.isPrimary) || instructorList[0];
            instance.mi_instructor = primaryInstructor.name;
          }
          
          // Add resource list to instance
          const resourceIdsForSession = sessionResourceMap[sessionId] || [];
          const resourceList = resourceIdsForSession.map(resourceId => ({
            id: resourceId,
            name: resourceNameMap[resourceId] || 'Unknown Resource'
          }));
          instance.resources = resourceList;
          
          // Add child sessions data for bundled sessions
          if (childSessionsMap[sessionId]) {
            instance.childSessions = childSessionsMap[sessionId];
            console.log(`✅ Added ${childSessionsMap[sessionId].length} child sessions to parent: ${instance.mi_sessionname}`);
          }
          
          // Adjust seats taken by subtracting account-only registrations (account set but no student)
          const accountOnlyCounts = accountOnlyCountMap[sessionId] || { placeholders: 0, waitlist: 0, total: 0 };
          
          // Store placeholder and waitlist counts for display in modal
          instance.placeholderCount = accountOnlyCounts.placeholders;
          instance.waitlistCount = accountOnlyCounts.waitlist;
          
          if (accountOnlyCounts.total > 0) {
            const originalSeatsTaken = instance.mi_seatstaken || 0;
            instance.mi_seatstaken = Math.max(0, originalSeatsTaken - accountOnlyCounts.total);
            console.log(`📊 Adjusted seats taken for "${instance.mi_sessionname}": ${originalSeatsTaken} - ${accountOnlyCounts.total} (${accountOnlyCounts.placeholders} placeholders + ${accountOnlyCounts.waitlist} waitlist) = ${instance.mi_seatstaken}`);
          }
        });
          
          allSessionInstances.push(...instances);
        }
      });

      console.log(`Created ${allSessionInstances.length} session instances from ${result.data.length} original sessions`);
      console.log('Sample transformed sessions:', allSessionInstances.slice(0, 3));
      console.log('Location mapping applied:', Object.keys(locationMap).length > 0);
      // Debug final data before returning
      console.log('Final session instances with location data:', 
        allSessionInstances.slice(0, 2).map(session => ({
          name: session[fieldMap.name],
          locationGuid: session._mi_location_value,
          locationName: session.locationName
        }))
);
      
      // Get all child session IDs to pass to frontend for filtering
      const allChildSessionIds = Object.values(bundleMap).flat().filter(Boolean);
      console.log('All child session IDs for filtering:', allChildSessionIds);
      console.log('=== END BUNDLE DEBUG ===');
      
      return { 
        success: true, 
        data: allSessionInstances,
        fieldMap: fieldMap,
        rawData: result.data.slice(0, 2),
        originalCount: result.data.length,
        instanceCount: allSessionInstances.length,
        locationMap: locationMap,
        childSessionIds: allChildSessionIds
      };
    } else {
      return result;
    }
  } catch (error) {
    console.error('Error fetching training sessions:', error);
    return { success: false, error: error.message };
  }
}

  async getRegistrationReport({ startDate, endDate } = {}) {
    try {
      console.log('=== REGISTRATION REPORT: Starting ===');
      
      // Step 1: Get all sessions with location and campus data
      const queryStartDate = startDate || '2023-01-01T00:00:00Z';
      const queryEndDate = endDate || '2025-12-31T23:59:59Z';
      
      const sessionEndpoint = `${this.sessionsEndpoint}?$select=mi_sessionid,mi_sessionname,mi_startdate,_mi_location_value,mi_billingresponsibility,mi_bundledsession&$filter=mi_startdate ge ${queryStartDate} and mi_startdate le ${queryEndDate}&$orderby=mi_startdate asc`;
      console.log('Fetching ALL sessions (with pagination) from:', this.sessionsEndpoint);
      const sessionResult = await this.fetchAllPages(sessionEndpoint, { failOnPageError: true });
      
      if (!sessionResult.success) {
        console.error('Failed to fetch sessions for report:', sessionResult.error);
        return { success: false, error: sessionResult.error };
      }
      
      const sessions = sessionResult.data;
      console.log(`Fetched ${sessions.length} sessions across ${sessionResult.totalPages} pages`);
      
      // Build session lookup map
      const sessionMap = {};
      sessions.forEach(s => {
        const locationFormatted = s['_mi_location_value@Microsoft.Dynamics.CRM.FormattedValue'] || 
                                   s['_mi_location_value@OData.Community.Display.V1.FormattedValue'] || '';
        sessionMap[s.mi_sessionid] = {
          id: s.mi_sessionid,
          name: s.mi_sessionname,
          startDate: s.mi_startdate,
          locationGuid: s._mi_location_value,
          locationFormatted: locationFormatted,
          campus: s['mi_billingresponsibility@Microsoft.Dynamics.CRM.FormattedValue'] || s['mi_billingresponsibility@OData.Community.Display.V1.FormattedValue'] || '',
          isBundled: s.mi_bundledsession || false
        };
      });
      
      // Step 2: Find bundled session relationships and apply counting rules
      // - "Basic Training" bundles: count children, skip parent
      // - All other bundles (Bootcamp, MSC, etc.): count parent, skip children
      const parentSessionIds = sessions
        .filter(s => s.mi_bundledsession)
        .map(s => s.mi_sessionid)
        .filter(Boolean);
      
      const basicTrainingParentIds = new Set();
      const basicTrainingChildIds = new Set();
      const otherBundleParentIds = new Set();
      const otherBundleChildIds = new Set();
      
      if (parentSessionIds.length > 0) {
        const batchSize = 25;
        for (let i = 0; i < parentSessionIds.length; i += batchSize) {
          const batch = parentSessionIds.slice(i, i + batchSize);
          const idFilters = batch.map(id => `_mi_parentsession_value eq ${id}`).join(' or ');
          const bundleEndpoint = `mi_bundledsessions?$select=_mi_includedsession_value,_mi_parentsession_value&$filter=${idFilters}`;
          
          const bundleResult = await this.makeDataverseRequest(bundleEndpoint);
          if (bundleResult.success) {
            bundleResult.data.forEach(b => {
              const parentId = b._mi_parentsession_value;
              const childId = b._mi_includedsession_value;
              if (!parentId || !childId) return;
              
              const parentSession = sessionMap[parentId];
              const parentName = (parentSession?.name || '').toLowerCase();
              
              if (parentName.includes('basic training')) {
                basicTrainingParentIds.add(parentId);
                basicTrainingChildIds.add(childId);
              } else {
                otherBundleParentIds.add(parentId);
                otherBundleChildIds.add(childId);
              }
            });
          }
        }
      }
      
      console.log(`Bundle rules: BT parents to skip: ${basicTrainingParentIds.size}, BT children to count: ${basicTrainingChildIds.size}, Other parents to count: ${otherBundleParentIds.size}, Other children to skip: ${otherBundleChildIds.size}`);
      
      // Step 3: Fetch location names for region mapping
      const locationGuids = [...new Set(sessions.map(s => s._mi_location_value).filter(Boolean))];
      let locationMap = {};
      
      if (locationGuids.length > 0) {
        locationMap = await this.getLocationNames(locationGuids);
      }
      
      console.log('Location map:', locationMap);
      
      // Step 4: First, test the registrations table to discover field names
      console.log('=== REGISTRATION TABLE DISCOVERY ===');
      let testRegResult = await this.makeDataverseRequest('mi_registrations?$top=3');
      let regTableFields = [];
      let regTableName = 'mi_registrations';
      
      // If plural fails, try singular
      if (!testRegResult.success || testRegResult.data.length === 0) {
        console.log('mi_registrations returned no data, trying mi_registration...');
        const singularResult = await this.makeDataverseRequest('mi_registration?$top=3');
        if (singularResult.success && singularResult.data.length > 0) {
          testRegResult = singularResult;
          regTableName = 'mi_registration';
          console.log('Found data using mi_registration (singular)');
        }
      }
      
      if (testRegResult.success && testRegResult.data.length > 0) {
        regTableFields = Object.keys(testRegResult.data[0]);
        console.log('Registration table name:', regTableName);
        console.log('Registration table fields:', regTableFields);
        console.log('Sample registration record:', testRegResult.data[0]);
        
        // Find the session lookup field
        const sessionField = regTableFields.find(f => f.includes('session') && f.startsWith('_'));
        const studentField = regTableFields.find(f => f.includes('student') && f.startsWith('_'));
        console.log('Session lookup field:', sessionField);
        console.log('Student lookup field:', studentField);
      } else {
        console.error('Failed to access registrations table:', testRegResult.error);
      }
      console.log('=== END REGISTRATION TABLE DISCOVERY ===');
      
      // Determine correct field names from discovery (filter out annotation keys containing '@')
      const rawFields = regTableFields.filter(f => !f.includes('@'));
      const sessionLookupField = rawFields.find(f => f.includes('session') && f.startsWith('_')) || '_mi_session_value';
      const studentLookupField = rawFields.find(f => f.includes('student') && f.startsWith('_')) || '_mi_student_value';
      const regStatusField = rawFields.find(f => f === 'mi_studentstatus') || rawFields.find(f => f.includes('registrationstatus')) || 'mi_studentstatus';
      
      console.log('Using fields:', { sessionLookupField, studentLookupField, regStatusField });
      
      let totalRegistrations = 0;
      
      // Initialize counters dynamically based on date range
      const rangeStartYear = parseInt(queryStartDate.substring(0, 4), 10);
      const rangeEndYear = parseInt(queryEndDate.substring(0, 4), 10);
      const years = [];
      const reportData = {};
      for (let y = rangeStartYear; y <= rangeEndYear; y++) {
        years.push(y);
        reportData[y] = { 'Washington': 0, 'Bootcamp (WA)': 0, 'South': 0, 'MI East': 0, 'Bootcamp (MI East)': 0, 'Online': 0, 'Total': 0 };
      }
      
      // Track details for debugging
      const locationRegionMap = {};
      const statusCounts = {};
      const otherExamples = [];
      
      // Fetch ONLY the registrations we need: those attached to an in-window
      // session (2023 onward). Filter server-side by session id, batched to keep
      // URLs short, instead of pulling the org's entire registration history and
      // discarding ~70% as "no session match".
      const regSelect = `$select=mi_registrationid,mi_registrationname,${sessionLookupField},${studentLookupField},statuscode,${regStatusField}`;
      const inWindowSessionIds = Object.keys(sessionMap);
      const allRegistrations = [];
      const regBatchSize = 20;
      console.log(`Fetching registrations for ${inWindowSessionIds.length} in-window sessions (batched)...`);
      for (let i = 0; i < inWindowSessionIds.length; i += regBatchSize) {
        const batch = inWindowSessionIds.slice(i, i + regBatchSize);
        const idFilter = batch.map(id => `${sessionLookupField} eq ${id}`).join(' or ');
        const regEndpoint = `${regTableName}?${regSelect}&$filter=${idFilter}`;
        const regPagesResult = await this.fetchAllPages(regEndpoint);
        if (regPagesResult.success) allRegistrations.push(...regPagesResult.data);
      }
      console.log(`Total registrations fetched: ${allRegistrations.length}`);
      
      // Process registrations against session map
      let matchedToSession = 0;
      let skippedNoSession = 0;
      let skippedBTParent = 0;
      let skippedOtherChild = 0;
      let skippedStatus = 0;
      let skippedYear = 0;
      
      allRegistrations.forEach(reg => {
        const sessionId = reg[sessionLookupField];
        const session = sessionMap[sessionId];
        
        if (!session) {
          skippedNoSession++;
          return;
        }
        
        // Skip Basic Training parents (count their children instead)
        if (basicTrainingParentIds.has(sessionId)) {
          skippedBTParent++;
          return;
        }
        
        // Skip children of non-Basic-Training bundles (count only the parent)
        if (otherBundleChildIds.has(sessionId)) {
          skippedOtherChild++;
          return;
        }
        
        // Filter out No Show and Cancelled by MI based on registration status formatted value
        const regStatusFormatted = reg[`${regStatusField}@Microsoft.Dynamics.CRM.FormattedValue`] || 
                                    reg[`${regStatusField}@OData.Community.Display.V1.FormattedValue`] || '';
        
        // Track all status values for debugging
        if (regStatusFormatted) {
          statusCounts[regStatusFormatted] = (statusCounts[regStatusFormatted] || 0) + 1;
        } else {
          statusCounts['(no status)'] = (statusCounts['(no status)'] || 0) + 1;
        }
        
        // Per CEO: exclude Dropped, No Show, Cancelled (all variants) and SA
        // Partial; count Completed, Retested & Passed, (no status), 90-Day and
        // Incomplete.
        const statusLower = regStatusFormatted.toLowerCase();
        if (statusLower.includes('no show') || statusLower.includes('noshow') || 
            statusLower.includes('cancel') || statusLower.includes('drop') ||
            statusLower.includes('sa partial')) {
          skippedStatus++;
          return;
        }
        
        // Determine year from session start date
        const year = new Date(session.startDate).getFullYear();
        if (year < rangeStartYear || year > rangeEndYear) {
          skippedYear++;
          return;
        }
        
        matchedToSession++;
        
        // Determine region from location (use GUID lookup, fall back to formatted value)
        const locationName = locationMap[session.locationGuid] || session.locationFormatted || '';
        const locationLower = locationName.toLowerCase().trim();
        const campusLower = (session.campus || '').toLowerCase();
        
        let region = 'Other';
        
        // Skip excluded locations and sessions with no recognized region
        if (locationLower === 'mama') return;
        
        // Check for Online first
        if (locationLower.includes('online')) {
          region = 'Online';
        }
        // Check for Washington locations
        else if (locationLower.includes('washington') || locationLower.includes(', wa') || 
                 locationLower.includes(' wa ') || locationLower.includes('seattle') ||
                 locationLower.includes('tacoma') || locationLower.includes('everett') ||
                 locationLower.includes('bellingham') || locationLower.includes('olympia') ||
                 locationLower.includes('anacortes') || locationLower.includes('edmonds') ||
                 locationLower.includes('burien') || locationLower.includes('kent') ||
                 locationLower.includes('renton') || locationLower === 'wa') {
          region = 'Washington';
        }
        // Check for Maritime Institute - West (South = MI West minus Online and WA, already filtered above)
        else if (campusLower.includes('west') || locationLower.includes('maritime institute - west') || locationLower.includes('maritime institute west')) {
          region = 'South';
        }
        // Check for Maritime Institute - East
        else if (campusLower.includes('east') || locationLower.includes('maritime institute - east') || locationLower.includes('maritime institute east')) {
          region = 'MI East';
        }
        
        // Skip unrecognized regions
        if (region === 'Other') return;
        
        // Check if this is a bootcamp - check session name and registration name
        const sessionNameLower = (session.name || '').toLowerCase();
        const regNameLower = (reg.mi_registrationname || '').toLowerCase();
        const isBootcamp = sessionNameLower.includes('bootcamp') || regNameLower.includes('bootcamp');
        
        // Segment bootcamp into separate categories for WA and MI East
        if (isBootcamp && region === 'Washington') {
          region = 'Bootcamp (WA)';
        } else if (isBootcamp && region === 'MI East') {
          region = 'Bootcamp (MI East)';
        }
        
        // Track location->region mapping for debugging
        if (locationName && !locationRegionMap[locationName]) {
          locationRegionMap[locationName] = { region, campus: session.campus };
        }
        
        reportData[year][region]++;
        reportData[year]['Total']++;
        totalRegistrations++;
      });
      
      console.log('=== REGISTRATION REPORT RESULTS ===');
      console.log('Report Data:', reportData);
      console.log('Total Registrations:', totalRegistrations);
      console.log('Registrations fetched from Dataverse:', allRegistrations.length);
      console.log('Matched to session in range:', matchedToSession);
      console.log('Skipped - no matching session:', skippedNoSession);
      console.log('Skipped - BT parent:', skippedBTParent);
      console.log('Skipped - other bundle child:', skippedOtherChild);
      console.log('Skipped - status filtered:', skippedStatus);
      console.log('Skipped - year out of range:', skippedYear);
      console.log('Status value counts:', statusCounts);
      console.log('Location -> Region mapping:', locationRegionMap);
      if (allRegistrations.length > 0) {
        console.log('Sample registration record:', allRegistrations[0]);
      }
      console.log('=== END REGISTRATION REPORT ===');
      
      return {
        success: true,
        data: reportData,
        years,
        totalRegistrations,
        locationRegionMap,
        childSessionsExcluded: basicTrainingParentIds.size + otherBundleChildIds.size,
        sessionsAnalyzed: sessions.length,
        bundleRules: {
          basicTrainingBundles: {
            rule: 'Count children, not parent',
            parentSessionCount: basicTrainingParentIds.size,
            childSessionCount: basicTrainingChildIds.size
          },
          otherBundles: {
            rule: 'Count parent only, not children',
            parentSessionCount: otherBundleParentIds.size,
            childSessionCount: otherBundleChildIds.size,
            types: 'Bootcamp, MSC Gov Vessels, and all other bundled courses'
          }
        },
        debug: {
          regTableName,
          regTableFields: rawFields.length > 0 ? rawFields.join(', ') : 'NONE - table not accessible',
          sessionLookupField,
          studentLookupField,
          totalFetched: allRegistrations.length,
          matchedToSession,
          skippedNoSession,
          skippedBTParent,
          skippedOtherChild,
          skippedStatus,
          skippedYear,
          statusCounts,
          otherExamples
        }
      };
    } catch (error) {
      console.error('Error generating registration report:', error);
      return { success: false, error: error.message };
    }
  }

  // Registrations by course × campus × hub × location (city) across the last 3
  // calendar years, for the CEO's gain/loss recovery analysis. The current year
  // is YTD (Jan 1 -> "today"/asOf); the two prior years are full calendar years
  // (the fetch window ends at today, so prior years — already complete — come in
  // full while the current year is naturally capped at the cutoff). Counting
  // mirrors getRegistrationReport (bundle rules + status exclusions), but buckets
  // by course/location instead of region so nothing is dropped for being outside
  // the WA/South/MI-East/Online regions.
  async getRegistrationsByCourseLocation({ asOf } = {}) {
    try {
      const now = asOf ? new Date(asOf) : new Date();
      const currentYear = now.getFullYear();
      // Current YTD year + 3 prior FULL calendar years (e.g. 2023-2026).
      const years = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
      const startDate = `${currentYear - 3}-01-01T00:00:00Z`;
      const endDate = now.toISOString(); // caps registration/session trends at the cutoff -> YTD
      const sessionStatusTrackingEnd = new Date('2026-12-31T23:59:59Z');
      const sessionQueryEnd = now < sessionStatusTrackingEnd ? sessionStatusTrackingEnd.toISOString() : endDate;

      const fmt = (o, f) =>
        o[`${f}@Microsoft.Dynamics.CRM.FormattedValue`] ||
        o[`${f}@OData.Community.Display.V1.FormattedValue`] || '';

      // 1. Sessions in range (with the mi_course lookup for real course identity).
      const sessionEndpoint = `${this.sessionsEndpoint}?$select=mi_sessionid,mi_sessionname,mi_startdate,_mi_course_value,_mi_location_value,mi_billingresponsibility,mi_bundledsession,cr38c_coursecancelled&$filter=mi_startdate ge ${startDate} and mi_startdate le ${sessionQueryEnd}&$orderby=mi_startdate asc`;
      const sessionResult = await this.fetchAllPages(sessionEndpoint, { failOnPageError: true });
      if (!sessionResult.success) return { success: false, error: sessionResult.error };
      const sessions = sessionResult.data;

      const sessionMap = {};
      sessions.forEach(s => {
        sessionMap[s.mi_sessionid] = {
          id: s.mi_sessionid,
          name: s.mi_sessionname || '',
          startDate: s.mi_startdate,
          courseGuid: s._mi_course_value || null,
          courseName: fmt(s, '_mi_course_value'),
          locationGuid: s._mi_location_value,
          locationFormatted: fmt(s, '_mi_location_value'),
          campus: fmt(s, 'mi_billingresponsibility'),
          isBundled: s.mi_bundledsession || false,
          cancelled: s.cr38c_coursecancelled || false
        };
      });

      // 2. Bundle rules: skip Basic Training parents (count children), skip other
      //    bundles' children (count parent) — identical to getRegistrationReport.
      const parentSessionIds = sessions.filter(s => s.mi_bundledsession).map(s => s.mi_sessionid).filter(Boolean);
      const basicTrainingParentIds = new Set();
      const basicTrainingChildIds = new Set();
      const otherBundleChildIds = new Set();
      if (parentSessionIds.length > 0) {
        const batchSize = 25;
        for (let i = 0; i < parentSessionIds.length; i += batchSize) {
          const batch = parentSessionIds.slice(i, i + batchSize);
          const idFilters = batch.map(id => `_mi_parentsession_value eq ${id}`).join(' or ');
          const bundleEndpoint = `mi_bundledsessions?$select=_mi_includedsession_value,_mi_parentsession_value&$filter=${idFilters}`;
          const bundleResult = await this.makeDataverseRequest(bundleEndpoint);
          if (!bundleResult.success) return { success: false, error: `Failed to load bundle relationships: ${bundleResult.error}` };
          bundleResult.data.forEach(b => {
            const parentId = b._mi_parentsession_value;
            const childId = b._mi_includedsession_value;
            if (!parentId || !childId) return;
            const parentName = (sessionMap[parentId]?.name || '').toLowerCase();
            if (parentName.includes('basic training')) {
              basicTrainingParentIds.add(parentId);
              basicTrainingChildIds.add(childId);
            } else otherBundleChildIds.add(childId);
          });
        }
      }

      // 3. Location names (GUID -> city).
      const locationGuids = [...new Set(sessions.map(s => s._mi_location_value).filter(Boolean))];
      const locationMap = locationGuids.length ? await this.getLocationNames(locationGuids) : {};

      // 4. Discover the registrations table + field names (as getRegistrationReport).
      let testRegResult = await this.makeDataverseRequest('mi_registrations?$top=3');
      let regTableName = 'mi_registrations';
      if (!testRegResult.success || testRegResult.data.length === 0) {
        const singular = await this.makeDataverseRequest('mi_registration?$top=3');
        if (singular.success && singular.data.length > 0) { testRegResult = singular; regTableName = 'mi_registration'; }
      }
      const regTableFields = (testRegResult.success && testRegResult.data.length > 0) ? Object.keys(testRegResult.data[0]) : [];
      const rawFields = regTableFields.filter(f => !f.includes('@'));
      const sessionLookupField = rawFields.find(f => f.includes('session') && f.startsWith('_')) || '_mi_session_value';
      const studentLookupField = rawFields.find(f => f.includes('student') && f.startsWith('_')) || '_mi_student_value';
      const regStatusField = rawFields.find(f => f === 'mi_studentstatus') || rawFields.find(f => f.includes('registrationstatus')) || 'mi_studentstatus';

      // 5. Fetch ONLY the registrations we need: those attached to an in-window
      //    session (2023 onward). We filter server-side by session id, batched
      //    to keep the URL length sane, instead of pulling the org's entire
      //    registration history and discarding ~70% as "no session match".
      const regSelect = `$select=mi_registrationid,mi_registrationname,${sessionLookupField},${studentLookupField},statuscode,${regStatusField}`;
      const inWindowSessionIds = Object.keys(sessionMap);
      const allRegistrations = [];
      const regBatchSize = 20;
      for (let i = 0; i < inWindowSessionIds.length; i += regBatchSize) {
        const batch = inWindowSessionIds.slice(i, i + regBatchSize);
        const idFilter = batch.map(id => `${sessionLookupField} eq ${id}`).join(' or ');
        const regEndpoint = `${regTableName}?${regSelect}&$filter=(${idFilter}) and ${studentLookupField} ne null and statuscode ne 2`;
        const regPagesResult = await this.fetchAllPages(regEndpoint, { failOnPageError: true });
        if (!regPagesResult.success) return { success: false, error: `Failed to load registrations: ${regPagesResult.error}` };
        allRegistrations.push(...regPagesResult.data);
      }

      // 6. Aggregate by course + campus + location, per year — BOTH the number
      //    of sessions (classes held) and the number of registrations
      //    (attendees), so the ratio (students/session) and attendance trends
      //    can be compared. Rows are keyed the same way for both metrics, and
      //    seeded from sessions so a class with zero registrations still shows.
      const normName = v => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
      const rowsMap = new Map();
      let totalRegistrations = 0;
      let totalSessions = 0;
      const unclassified = new Set();
      const sessionStatusYears = [2025, 2026];
      const sessionStatusTrackingStart = new Date('2025-01-01T00:00:00Z');
      const sessionStatusByCourseLocationMap = new Map();

      // Specific course names excluded from this report entirely (per CEO).
      // Matched on the normalized (trim + lowercase + collapse-whitespace) course
      // name so minor spacing differences don't slip through.
      const EXCLUDED_COURSE_NAMES = new Set([
        '24 hour medical care provider',
        '4 hour personal safety & social responsibility',
        'able seaman- historical',
        'twic card',
        'untitled',
        'merchant mariner credential',
        'mama examination retake',
        "captain's course (6-pack/100ton) (classroom)",
        'gov all (6) courses',
        'high voltage safety historical',
        'proficiency in survival craft (lifeboatman)',
        'proficiency in survival craft refresher (lifeboatman)',
        'qmed electrician/ refrigeration',
        'qmed fireman/oiler/watertender',
        'radar observer unlimited (radu)',
        'ratings forming part of an engineering watch (rfpew)',
        'visual communications (flashing light)',
        'vessel security officer (vso)',
        'upgrade oupv to master 100 tons (online)'
      ].map(normName));

      // Courses excluded from this report entirely:
      //   - The explicit EXCLUDED_COURSE_NAMES list above (per CEO).
      //   - "Yacht Charter" (per CEO: not part of the standard course list)
      //   - "Basic Training" (a bundle that must be broken out into its child
      //     classes; its parent sessions are skipped and children counted under
      //     their own course names, so no "Basic Training" row should appear)
      //   - Session codes like "MSCRR011323SD" (course abbrev + MMDDYY date +
      //     location code) that leak through when a session has no real mi_course
      //     lookup. Also catches human-readable session titles in the format
      //     "Course Name M/D/YYYY City, ST". These are session names, not courses.
      const isExcludedCourse = name => {
        const n = String(name || '').toLowerCase();
        if (EXCLUDED_COURSE_NAMES.has(normName(name))) return true;
        if (n.includes('yacht') || n.includes('basic training')) return true;
        const hasCompactSessionCode = /[a-z]\d{5,}|\d{5,}[a-z]/i.test(n);
        const hasDatedLocationSuffix = /\b(?:0?[1-9]|1[0-2])[/-](?:0?[1-9]|[12]\d|3[01])[/-](?:19|20)\d{2}\s+.+,\s*[a-z]{2}(?:\s+\d{5}(?:-\d{4})?)?\s*$/i.test(n);
        return hasCompactSessionCode || hasDatedLocationSuffix;
      };

      // Locations excluded from this report entirely (test/junk data).
      const isExcludedLocation = loc => {
        const l = String(loc || '').trim().toLowerCase();
        return l === 'mama' || /\bmama\b/.test(l);
      };

      // The status comparison counts the Basic Training parent once and omits
      // its children. Other tabs retain their existing inverse rule.
      const resolveStatusCourse = (session) => {
        const rawCourseName = (session.courseName || session.name || 'Untitled').trim() || 'Untitled';
        const isBasicTrainingParent = basicTrainingParentIds.has(session.id);
        if (!isBasicTrainingParent && isExcludedCourse(rawCourseName)) return null;
        const location = (locationMap[session.locationGuid] || session.locationFormatted || '').trim();
        const campus = (session.campus || '').trim();
        if (isExcludedLocation(location)) return null;
        if (!location && !campus) return null;
        const hub = classifyHub(location, campus) || 'Unclassified';
        if (isBasicTrainingParent) {
          return {
            statusKey: `status:basic-training||${campus}||${location}`,
            courseName: 'BASIC TRAINING', campus, hub, location
          };
        }
        const courseKey = session.courseGuid || `name:${normName(rawCourseName)}`;
        return {
          statusKey: `${courseKey}||${campus}||${location}`,
          courseName: rawCourseName, campus, hub, location
        };
      };

      // Resolve the display fields + row for a session (creating it on first
      // use). Returns null when the session's course or location is excluded,
      // so excluded items never even appear as an empty (zero-count) row.
      const resolveRow = (session) => {
        const courseName = (session.courseName || session.name || 'Untitled').trim() || 'Untitled';
        if (isExcludedCourse(courseName)) return null;
        const location = (locationMap[session.locationGuid] || session.locationFormatted || '').trim();
        if (isExcludedLocation(location)) return null;
        const campus = (session.campus || '').trim();
        if (!location && !campus) return null;
        const hubCode = classifyHub(location, campus);
        const hub = hubCode || 'Unclassified';
        if (!hubCode && (location || campus)) unclassified.add(location || campus);
        const courseKey = session.courseGuid || `name:${normName(courseName)}`;
        const rowKey = `${courseKey}||${campus}||${location}`;
        if (!rowsMap.has(rowKey)) {
          rowsMap.set(rowKey, {
            course: courseName, courseGuid: session.courseGuid, campus, location, hub,
            counts: {}, total: 0,             // registrations (attendees)
            sessionCounts: {}, sessionTotal: 0 // sessions (classes held)
          });
        }
        return { row: rowsMap.get(rowKey), location, courseKey, courseName };
      };

      // Sessions (classes held). Apply the same bundle skip rules so the counted
      // unit matches the registration counting.
      sessions.forEach(s => {
        const session = sessionMap[s.mi_sessionid];
        if (!session) return;
        if (otherBundleChildIds.has(session.id)) return;
        const year = new Date(session.startDate).getFullYear();
        if (!years.includes(year)) return;

        const sessionDate = new Date(session.startDate);
        if (!basicTrainingChildIds.has(session.id) && sessionDate >= sessionStatusTrackingStart && sessionStatusYears.includes(year)) {
          const statusCourse = resolveStatusCourse(session);
          if (statusCourse) {
            const { statusKey, courseName, campus, hub, location } = statusCourse;
            if (!sessionStatusByCourseLocationMap.has(statusKey)) {
              sessionStatusByCourseLocationMap.set(statusKey, {
                course: courseName, campus, hub, location,
                scheduled: { 2025: 0, 2026: 0 },
                cancelled: { 2025: 0, 2026: 0 },
                held: { 2025: 0, 2026: 0 }
              });
            }
            const statusRow = sessionStatusByCourseLocationMap.get(statusKey);
            // "Scheduled" is the total number of sessions placed on the
            // calendar. Cancelled and Held are outcome subsets, not competing
            // statuses, so prior years retain their meaningful scheduled count.
            statusRow.scheduled[year] += 1;
            if (session.cancelled) statusRow.cancelled[year] += 1;
            else if (sessionDate <= now) statusRow.held[year] += 1;
          }
        }

        if (basicTrainingParentIds.has(session.id)) return;
        const resolved = resolveRow(session);
        if (!resolved) return; // excluded course / location (Yacht, Basic Training, session codes, MAMA)
        const { row } = resolved;

        // The original course/location trend remains YTD. Sessions later in
        // 2026 are fetched only so the status comparison can show the full
        // scheduled year.
        if (sessionDate > now) return;
        if (session.cancelled) return;
        row.sessionCounts[year] = (row.sessionCounts[year] || 0) + 1;
        row.sessionTotal += 1;
        totalSessions += 1;
      });

      // Registrations (attendees).
      allRegistrations.forEach(reg => {
        const sessionId = reg[sessionLookupField];
        const session = sessionMap[sessionId];
        if (!session) return;
        if (basicTrainingParentIds.has(sessionId)) return;
        if (otherBundleChildIds.has(sessionId)) return;

        // Registration-status rule (per CEO): a "seat taken" is any registration
        // whose student was NOT dropped, no-showed, or cancelled.
        //   EXCLUDED : Dropped, No Show, Cancelled (all variants), SA Partial
        //   COUNTED  : Completed, Retested & Passed, (no status), 90-Day,
        //              Incomplete  (everything not excluded)
        const statusLower = (
          reg[`${regStatusField}@Microsoft.Dynamics.CRM.FormattedValue`] ||
          reg[`${regStatusField}@OData.Community.Display.V1.FormattedValue`] || ''
        ).toLowerCase();
        const isExcludedStatus =
          statusLower.includes('drop') ||
          statusLower.includes('no show') || statusLower.includes('noshow') ||
          statusLower.includes('cancel') || // covers "Cancelled" and "Cancelled by MI"
          statusLower.includes('sa partial');
        if (isExcludedStatus) return;

        const year = new Date(session.startDate).getFullYear();
        if (!years.includes(year)) return;
        if (new Date(session.startDate) > now) return;

        const resolved = resolveRow(session);
        if (!resolved) return; // excluded course / location (Yacht, Basic Training, session codes, MAMA)
        const { row } = resolved;
        row.counts[year] = (row.counts[year] || 0) + 1;
        row.total += 1;
        totalRegistrations += 1;
      });

      // Status comparison rows are built separately so future sessions do not
      // seed empty rows in the YTD registration/session trend tabs.
      const rows = [...rowsMap.values()].filter(row => row.total > 0 || row.sessionTotal > 0);
      const sessionStatusByCourse = [...sessionStatusByCourseLocationMap.values()];
      return {
        success: true,
        rows,
        years,
        currentYear,
        asOf: endDate.slice(0, 10),
        totalRegistrations,
        totalSessions,
        sessionsAnalyzed: sessions.length,
        sessionStatusYears,
        sessionStatusTrackingStart: '2025-01-01',
        sessionStatusTrackingEnd: '2026-12-31',
        sessionStatusByCourse,
        unclassified: [...unclassified].sort((a, b) => a.localeCompare(b))
      };
    } catch (error) {
      console.error('Error generating course/location registration report:', error);
      return { success: false, error: error.message };
    }
  }

  // Login tracking methods
  async recordLogin(account) {
    if (!account) return { success: false, error: 'No account provided' };
    try {
      const result = await this.makeDataverseRequest('cr38c_logintrackings', {
        method: 'POST',
        body: JSON.stringify({
          cr38c_name: account.name || 'Unknown',
          cr38c_email: account.username || 'Unknown',
          cr38c_timestamp: new Date().toISOString()
        })
      });
      return result;
    } catch (error) {
      console.error('Failed to record login:', error);
      return { success: false, error: error.message };
    }
  }

  async getLoginRecords() {
    try {
      const result = await this.makeDataverseRequest(
        'cr38c_logintrackings?$select=cr38c_name,cr38c_email,cr38c_timestamp&$orderby=cr38c_timestamp desc'
      );
      return result;
    } catch (error) {
      console.error('Failed to fetch login records:', error);
      return { success: false, error: error.message };
    }
  }

  async getSessionsSchema() {
    return this.discoverTableSchema();
  }

  async testConnection() {
    try {
      const result = await this.makeDataverseRequest('WhoAmI()');
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ── New table methods (Instructor Quals, Site Approvals, Guardrails) ──

  // Fetch all active course types
  async getCourseTypes() {
    try {
      const endpoint = 'mi_coursetypes?$select=mi_coursetypeid,mi_coursetypename,mi_coursetypecode,mi_defaultdurationdays&$filter=statecode eq 0&$orderby=mi_coursetypename asc';
      const result = await this.makeDataverseRequest(endpoint);
      if (result.success) {
        return {
          success: true,
          data: result.data.map(ct => ({
            id: ct.mi_coursetypeid,
            name: ct.mi_coursetypename,
            code: ct.mi_coursetypecode,
            defaultDurationDays: ct.mi_defaultdurationdays
          }))
        };
      }
      return result;
    } catch (error) {
      console.error('Error fetching course types:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  // Fetch instructor qualifications, optionally filtered by instructor or course type
  async getInstructorQualifications(filters = {}) {
    try {
      let filterParts = ['statecode eq 0', 'mi_isactive eq true'];
      if (filters.instructorId) {
        filterParts.push(`_mi_instructor_value eq ${filters.instructorId}`);
      }
      if (filters.courseTypeId) {
        filterParts.push(`_mi_coursetype_value eq ${filters.courseTypeId}`);
      }

      const endpoint = `mi_instructorqualifications?$select=mi_instructorqualificationid,_mi_instructor_value,_mi_coursetype_value,mi_qualificationdate,mi_expirationdate,mi_isactive&$filter=${filterParts.join(' and ')}`;
      const result = await this.makeDataverseRequest(endpoint);
      if (result.success) {
        return {
          success: true,
          data: result.data.map(q => ({
            id: q.mi_instructorqualificationid,
            instructorId: q._mi_instructor_value,
            instructorName: q['_mi_instructor_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            courseTypeId: q._mi_coursetype_value,
            courseTypeName: q['_mi_coursetype_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            qualificationDate: q.mi_qualificationdate,
            expirationDate: q.mi_expirationdate,
            isActive: q.mi_isactive
          }))
        };
      }
      return result;
    } catch (error) {
      console.error('Error fetching instructor qualifications:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  // Fetch qualified instructors for a specific course type
  async getQualifiedInstructorsForCourse(courseTypeId) {
    return this.getInstructorQualifications({ courseTypeId });
  }

  // Fetch instructor credentials
  async getInstructorCredentials(instructorId) {
    try {
      let filter = 'statecode eq 0';
      if (instructorId) {
        filter += ` and _mi_instructor_value eq ${instructorId}`;
      }

      const endpoint = `mi_instructorcredentials?$select=mi_instructorcredentialid,_mi_instructor_value,mi_credentialtitle,mi_credentialnumber,mi_issuedate,mi_expirationdate&$filter=${filter}`;
      const result = await this.makeDataverseRequest(endpoint);
      if (result.success) {
        return {
          success: true,
          data: result.data.map(c => ({
            id: c.mi_instructorcredentialid,
            instructorId: c._mi_instructor_value,
            instructorName: c['_mi_instructor_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            title: c.mi_credentialtitle,
            number: c.mi_credentialnumber,
            issueDate: c.mi_issuedate,
            expirationDate: c.mi_expirationdate
          }))
        };
      }
      return result;
    } catch (error) {
      console.error('Error fetching instructor credentials:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  // Fetch site approvals, optionally filtered by location or course type
  async getSiteApprovals(filters = {}) {
    try {
      let filterParts = ['statecode eq 0'];
      if (filters.locationId) {
        filterParts.push(`_mi_location_value eq ${filters.locationId}`);
      }
      if (filters.courseTypeId) {
        filterParts.push(`_mi_coursetype_value eq ${filters.courseTypeId}`);
      }

      const endpoint = `mi_siteapprovals?$select=mi_siteapprovalid,_mi_location_value,_mi_coursetype_value,mi_maxstudents,mi_approvalstatus,mi_approvaldate,mi_expirationdate&$filter=${filterParts.join(' and ')}`;
      const result = await this.makeDataverseRequest(endpoint);
      if (result.success) {
        return {
          success: true,
          data: result.data.map(sa => ({
            id: sa.mi_siteapprovalid,
            locationId: sa._mi_location_value,
            locationName: sa['_mi_location_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            courseTypeId: sa._mi_coursetype_value,
            courseTypeName: sa['_mi_coursetype_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            maxStudents: sa.mi_maxstudents,
            approvalStatus: sa.mi_approvalstatus,
            approvalStatusName: sa['mi_approvalstatus@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            approvalDate: sa.mi_approvaldate,
            expirationDate: sa.mi_expirationdate
          }))
        };
      }
      return result;
    } catch (error) {
      console.error('Error fetching site approvals:', error);
      return { success: false, error: error.message, data: [] };
    }
  }

  // Get the CCA max capacity for a specific location + course type pair
  async getSiteMaxCapacity(locationId, courseTypeId) {
    const result = await this.getSiteApprovals({ locationId, courseTypeId });
    if (result.success && result.data.length > 0) {
      return { success: true, maxStudents: result.data[0].maxStudents };
    }
    return { success: false, maxStudents: null };
  }

  // Fetch all active scheduling rules
  async getSchedulingRules() {
    try {
      const endpoint = 'mi_schedulingrules?$select=mi_schedulingruleid,mi_rulename,mi_ruletype,_mi_coursetype1_value,_mi_coursetype2_value,_mi_location1_value,_mi_location2_value,mi_regionscope,mi_mingapdays,mi_isenabled,mi_description&$filter=statecode eq 0 and mi_isenabled eq true';
      const result = await this.makeDataverseRequest(endpoint);
      if (result.success) {
        return {
          success: true,
          data: result.data.map(r => ({
            id: r.mi_schedulingruleid,
            name: r.mi_rulename,
            ruleType: r.mi_ruletype,
            ruleTypeName: r['mi_ruletype@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            courseType1Id: r._mi_coursetype1_value,
            courseType1Name: r['_mi_coursetype1_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            courseType2Id: r._mi_coursetype2_value,
            courseType2Name: r['_mi_coursetype2_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            location1Id: r._mi_location1_value,
            location1Name: r['_mi_location1_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            location2Id: r._mi_location2_value,
            location2Name: r['_mi_location2_value@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            regionScope: r.mi_regionscope,
            regionScopeName: r['mi_regionscope@Microsoft.Dynamics.CRM.FormattedValue'] || '',
            minGapDays: r.mi_mingapdays,
            isEnabled: r.mi_isenabled,
            description: r.mi_description
          }))
        };
      }
      return result;
    } catch (error) {
      console.error('Error fetching scheduling rules:', error);
      return { success: false, error: error.message, data: [] };
    }
  }
}

const authenticatedDataverseService = new AuthenticatedDataverseService();
export default authenticatedDataverseService;
